import React, { useState, useCallback, useEffect, useRef } from "react";
import DataGrid, {
  Column, Export, FilterRow, HeaderFilter, FilterPanel,
  Scrolling, LoadPanel, SearchPanel, ColumnChooser,
  ColumnChooserSearch, ColumnChooserSelection, Position,
  Paging, Pager, StateStoring,
} from "devextreme-react/data-grid";
import Button1 from "@material-ui/core/Button";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { exportDataGrid } from "devextreme/excel_exporter";
import "whatwg-fetch";
import swal from "sweetalert";
import { useStyles } from "../../Styles.js";
import Grid from "@material-ui/core/Grid";
import Container from "@material-ui/core/Container";
import useDeviceSize from "../../hooks/useDeviceSize.js";
import { api_path } from "../../shared.js";
import { FMCUserGuide } from "./FMCUserGuide.jsx";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const PAGE_SIZES = [25, 50, 100];
const SEARCH_OPTIONS = { placeholder: "Search column" };
const getUserID = JSON.parse(localStorage.getItem("USER_ID"));
const COMPARE_FIELDS = [
  "hs_code", "commodity",
  "port_of_loading", "port_of_discharge",
  "final_destination", "container_status", "consignee_country",
  "rate_base", "ocean_freight_amount", "item_currency", "ar_currency",
];

const STATUS_META = {
  CHANGED: { color: "#d97706", bg: "#fffbeb", label: "CHANGED" },
  NEW: { color: "#16a34a", bg: "#f0fdf4", label: "NEW" },
  REMOVED: { color: "#dc2626", bg: "#fef2f2", label: "REMOVED" },
  SAME: { color: "#94a3b8", bg: "transparent", label: "SAME" },
};
const token = process.env.REACT_APP_API_TOKEN || "";
const COLUMNS = [
  { field: "hs_code", cap: "HS Code", w: 110 },
  { field: "commodity", cap: "Commodity" },
  { field: "port_of_loading", cap: "Port of Loading", w: 200 },
  { field: "port_of_discharge", cap: "Port of Discharge", w: 200 },
  { field: "final_destination", cap: "Destination", w: 200 },
  { field: "container_status", cap: "Services Type", w: 150 },
  { field: "rate_base", cap: "Rate Base", w: 130 },
  { field: "ocean_freight_amount", cap: "Selling Rate", w: 130, txtFmt: "currency" },
];

const EXCEL_COLUMNS = [
  { key: "id", header: "_row_id_" },
  { key: "hs_code", header: "HS Code" },
  { key: "commodity", header: "Commodity" },
  { key: "port_of_loading", header: "Port of Loading" },
  { key: "port_of_discharge", header: "Port of Discharge" },
  { key: "final_destination", header: "Destination" },
  { key: "container_status", header: "Services Type" },
  { key: "rate_base", header: "Rate Base" },
  { key: "ocean_freight_amount", header: "Selling Rate" },
];

// ─────────────────────────────────────────────────────────────────────────────
// FORMAT HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const makeCurrencyText = ({ value }) => {
  if (value == null || value === "") return "";
  const n = parseFloat(value);
  return isNaN(n) ? value
    : "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (d) =>
  d.toLocaleDateString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" })
    .split("/").reverse().join("-");

const now = new Date();

const yearOptions = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i);
const monthOptions = [
  { v: 1, l: "มกราคม" }, { v: 2, l: "กุมภาพันธ์" },
  { v: 3, l: "มีนาคม" }, { v: 4, l: "เมษายน" },
  { v: 5, l: "พฤษภาคม" }, { v: 6, l: "มิถุนายน" },
  { v: 7, l: "กรกฎาคม" }, { v: 8, l: "สิงหาคม" },
  { v: 9, l: "กันยายน" }, { v: 10, l: "ตุลาคม" },
  { v: 11, l: "พฤศจิกายน" }, { v: 12, l: "ธันวาคม" },
];

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT EXCEL HELPER
// ─────────────────────────────────────────────────────────────────────────────
async function exportToExcel(data, fileName) {
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet("US Shipment");
  worksheet.columns = EXCEL_COLUMNS.map(c => ({ header: c.header, key: c.key, width: 22 }));
  worksheet.getColumn(1).hidden = true;
  worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  worksheet.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E79" } };
  worksheet.getRow(1).alignment = { vertical: "middle", horizontal: "center" };
  data.forEach((row, i) => {
    const r = worksheet.addRow(EXCEL_COLUMNS.map(c => {
      const val = row[c.key] ?? "";
      if (c.key === "ocean_freight_amount" && val !== "") {
        const n = parseFloat(String(val).replace(/,/g, ""));
        return isNaN(n) ? val : "$ " + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return val;
    }));
    if (i % 2 === 1) r.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F9FC" } };
  });
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName);
}

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const Toast = ({ toast }) => {
  if (!toast) return null;
  return (
    <div style={{
      position: "fixed", top: 16, right: 16, zIndex: 9999,
      background: toast.type === "error" ? "#fef2f2" : "#f0fdf4",
      color: toast.type === "error" ? "#dc2626" : "#15803d",
      border: `1px solid ${toast.type === "error" ? "#fca5a5" : "#86efac"}`,
      borderRadius: 10, padding: "10px 20px",
      fontWeight: 700, fontSize: 13, boxShadow: "0 4px 16px #0002",
    }}>{toast.msg}</div>
  );
};

const StatusBadge = ({ data }) => {
  const s = STATUS_META[data?.diff_status];
  if (!s) return null;
  return (
    <span style={{
      background: s.color + "22", color: s.color,
      border: `1px solid ${s.color}55`,
      borderRadius: 4, padding: "1px 7px",
      fontSize: 10, fontWeight: 800, whiteSpace: "nowrap",
    }}>{s.label}</span>
  );
};

const SummaryBar = ({ summary, activeFilter, onFilter, snapshotInfo }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 8,
    flexWrap: "wrap", margin: "10px 0", padding: "8px 12px",
    background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0",
  }}>
    {snapshotInfo && (
      <span style={{ fontSize: 11, color: "#64748b", marginRight: 4 }}>
        📋 เทียบกับ: <strong>{snapshotInfo}</strong>
      </span>
    )}
    <div style={{ width: 1, height: 18, background: "#e2e8f0" }} />
    {["CHANGED", "NEW", "REMOVED", "SAME"].map(key => {
      const m = STATUS_META[key], count = summary?.[key.toLowerCase()] ?? 0, active = activeFilter === key;
      return (
        <span key={key} onClick={() => onFilter(active ? "ALL" : key)} style={{
          background: active ? m.color : m.bg || "#f8fafc", color: active ? "#fff" : m.color,
          border: `1px solid ${m.color}55`, borderRadius: 6, padding: "3px 12px",
          fontSize: 11, fontWeight: 800, cursor: "pointer", transition: "all .15s", userSelect: "none",
        }}>{m.label} {count}</span>
      );
    })}
    {activeFilter !== "ALL" && (
      <span onClick={() => onFilter("ALL")} style={{
        fontSize: 11, color: "#6366f1", cursor: "pointer", textDecoration: "underline",
      }}>ดูทั้งหมด ({summary?.total ?? 0})</span>
    )}
  </div>
);

// ─── Snapshot Log Modal ───────────────────────────────────────────────────────
const SnapshotLogModal = ({ open, onClose, showToast }) => {
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [exportingId, setExportingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoadingLogs(true);
    fetch(`${api_path}/us-shipment/snapshot-logs.php`)
      .then(r => r.json())
      .then(j => setLogs(j.logs ?? []))
      .catch(() => showToast("❌ โหลด log ไม่สำเร็จ", "error"))
      .finally(() => setLoadingLogs(false));
  }, [open, showToast]);

  const handleExportSnapshot = useCallback(async (log) => {
    setExportingId(log.id);
    try {
      const res = await fetch(`${api_path}/us-shipment/get-snapshot.php?id=${log.id}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error ?? "Load failed");
      await exportToExcel(json.data, `get_snapshot_${log.file_name}.xlsx`);
      showToast(`📥 Export Snapshot สำเร็จ`);
    } catch (e) {
      showToast("❌ Export ไม่สำเร็จ: " + e.message, "error");
    }
    setExportingId(null);
  }, [showToast]);

  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1300, background: "#0006", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "min(900px, 95vw)", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 40px #0003" }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 800, fontSize: 16, color: "#1e293b" }}>📋 ประวัติการบันทึก Snapshot</span>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}>✕</button>
        </div>
        <div style={{ overflowY: "auto", padding: "16px 24px", flex: 1 }}>
          {loadingLogs ? (
            <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>⏳ กำลังโหลด...</div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>ยังไม่มีประวัติการบันทึก</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f1f5f9" }}>
                  {["#", "ชื่อ Snapshot", "วันที่บันทึก", "จำนวน Row", "บันทึกโดย", "หมายเหตุ", ""].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "#475569", borderBottom: "2px solid #e2e8f0" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={log.id} style={{ background: i % 2 === 1 ? "#f8fafc" : "#fff" }}>
                    <td style={{ padding: "8px 12px", color: "#94a3b8" }}>{i + 1}</td>
                    <td style={{ padding: "8px 12px", fontWeight: 600, color: "#1e293b" }}>{log.file_name}</td>
                    <td style={{ padding: "8px 12px", color: "#475569" }}>{log.created_at}</td>
                    <td style={{ padding: "8px 12px", color: "#475569", textAlign: "right" }}>{log.row_count?.toLocaleString()}</td>
                    <td style={{ padding: "8px 12px", color: "#475569" }}>{log.created_by ?? "-"}</td>
                    <td style={{ padding: "8px 12px", color: "#64748b", fontSize: 12 }}>{log.remark ?? "-"}</td>
                    <td style={{ padding: "8px 12px" }}>
                      <button onClick={() => handleExportSnapshot(log)} disabled={exportingId === log.id}
                        style={{ background: exportingId === log.id ? "#e2e8f0" : "#eff6ff", color: exportingId === log.id ? "#94a3b8" : "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 700, cursor: exportingId === log.id ? "default" : "pointer" }}>
                        {exportingId === log.id ? "⏳" : "📥 Export"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Update Log Modal ─────────────────────────────────────────────────────────
const UpdateLogModal = ({ open, onClose, showToast }) => {
  const [logs, setLogs] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterSnap, setFilterSnap] = useState("");
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [details, setDetails] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${api_path}/us-shipment/update-logs.php`)
      .then(r => r.json())
      .then(j => { setLogs(j.logs ?? []); setSnapshots(j.snapshots ?? []); })
      .catch(() => showToast("❌ โหลด update log ไม่สำเร็จ", "error"))
      .finally(() => setLoading(false));
  }, [open, showToast]);

  const handleFilterSnap = useCallback((snapId) => {
    setFilterSnap(snapId);
    setLoading(true);
    const url = snapId
      ? `${api_path}/us-shipment/update-logs.php?snapshot_id=${snapId}`
      : `${api_path}/us-shipment/update-logs.php`;
    fetch(url)
      .then(r => r.json())
      .then(j => setLogs(j.logs ?? []))
      .catch(() => showToast("❌ โหลดไม่สำเร็จ", "error"))
      .finally(() => setLoading(false));
  }, [showToast]);

  const handleExpand = useCallback(async (logId) => {
    if (expandedLogId === logId) { setExpandedLogId(null); return; }
    setExpandedLogId(logId);
    if (details[logId]) return;
    setLoadingDetail(logId);
    try {
      const res = await fetch(`${api_path}/us-shipment/update-logs.php?log_id=${logId}`);
      const json = await res.json();
      if (json.success) setDetails(prev => ({ ...prev, [logId]: json.details ?? [] }));
    } catch { }
    setLoadingDetail(null);
  }, [expandedLogId, details]);

  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1300, background: "#0006", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "min(1000px, 95vw)", maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 40px #0003" }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 800, fontSize: 16, color: "#1e293b" }}>📝 ประวัติการ Update Snapshot</span>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}>✕</button>
        </div>
        <div style={{ padding: "12px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>กรอง Snapshot:</span>
          <select value={filterSnap} onChange={e => handleFilterSnap(e.target.value)}
            style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, color: "#1e293b", minWidth: 240 }}>
            <option value="">-- แสดงทั้งหมด --</option>
            {snapshots.map(s => <option key={s.id} value={s.id}>{s.file_name}</option>)}
          </select>
          <span style={{ fontSize: 12, color: "#94a3b8" }}>พบ {logs.length} รายการ</span>
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>⏳ กำลังโหลด...</div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>ยังไม่มีประวัติการ Update</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f1f5f9", position: "sticky", top: 0 }}>
                  {["", "#", "Snapshot", "ไฟล์ที่ Upload", "แก้ไข", "ไม่เปลี่ยน", "ไม่พบ", "อัปเดตโดย", "วันที่"].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "#475569", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <React.Fragment key={log.id}>
                    <tr style={{ background: i % 2 === 1 ? "#f8fafc" : "#fff", cursor: "pointer" }} onClick={() => handleExpand(log.id)}>
                      <td style={{ padding: "8px 12px", color: "#6366f1", fontWeight: 700, fontSize: 16 }}>{expandedLogId === log.id ? "▾" : "▸"}</td>
                      <td style={{ padding: "8px 12px", color: "#94a3b8" }}>{i + 1}</td>
                      <td style={{ padding: "8px 12px", fontWeight: 600, color: "#1e293b" }}>{log.snapshot_name ?? "-"}</td>
                      <td style={{ padding: "8px 12px", color: "#475569", fontSize: 12 }}>{log.upload_file}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ background: "#f0fdf4", color: "#16a34a", borderRadius: 4, padding: "2px 8px", fontWeight: 700 }}>{log.updated}</span>
                      </td>
                      <td style={{ padding: "8px 12px", color: "#94a3b8" }}>{log.unchanged}</td>
                      <td style={{ padding: "8px 12px" }}>
                        {log.not_found > 0
                          ? <span style={{ background: "#fef2f2", color: "#dc2626", borderRadius: 4, padding: "2px 8px", fontWeight: 700 }}>{log.not_found}</span>
                          : <span style={{ color: "#94a3b8" }}>0</span>}
                      </td>
                      <td style={{ padding: "8px 12px", color: "#475569" }}>{log.updated_by ?? "-"}</td>
                      <td style={{ padding: "8px 12px", color: "#475569", fontSize: 12 }}>{log.updated_at}</td>
                    </tr>
                    {expandedLogId === log.id && (
                      <tr>
                        <td colSpan={9} style={{ padding: 0, background: "#f8fafc" }}>
                          <div style={{ padding: "8px 24px 16px 40px" }}>
                            {loadingDetail === log.id ? (
                              <div style={{ color: "#64748b", fontSize: 12, padding: 8 }}>⏳ กำลังโหลด detail...</div>
                            ) : (details[log.id] ?? []).length === 0 ? (
                              <div style={{ color: "#94a3b8", fontSize: 12, padding: 8 }}>ไม่มี detail</div>
                            ) : (
                              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                <thead>
                                  <tr style={{ background: "#e2e8f0" }}>
                                    {["Rate ID", "Field", "ค่าเดิม", "ค่าใหม่"].map(h => (
                                      <th key={h} style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "#475569" }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(details[log.id] ?? []).map((d, di) => (
                                    <tr key={d.id} style={{ background: di % 2 === 1 ? "#f1f5f9" : "#fff" }}>
                                      <td style={{ padding: "5px 10px", color: "#374151", fontWeight: 600 }}>{d.rate_id}</td>
                                      <td style={{ padding: "5px 10px", color: "#6366f1", fontWeight: 600 }}>{d.field_name}</td>
                                      <td style={{ padding: "5px 10px", color: "#dc2626" }}>{d.old_value ?? "-"}</td>
                                      <td style={{ padding: "5px 10px", color: "#16a34a" }}>{d.new_value ?? "-"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Update Snapshot Modal ────────────────────────────────────────────────────
const UpdateSnapshotModal = ({ open, onClose, showToast }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [loadingSnap, setLoadingSnap] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoadingSnap(true);
    setFile(null);
    setUpdateResult(null);
    fetch(`${api_path}/us-shipment/snapshot-logs.php`)
      .then(r => r.json())
      .then(j => {
        const logs = j.logs ?? [];
        setSnapshots(logs);
        setSelectedId(logs.length > 0 ? String(logs[0].id) : "");
      })
      .catch(() => showToast("❌ โหลด snapshot list ไม่สำเร็จ", "error"))
      .finally(() => setLoadingSnap(false));
  }, [open, showToast]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f && !f.name.endsWith(".xlsx")) { showToast("❌ รองรับเฉพาะไฟล์ .xlsx เท่านั้น", "error"); return; }
    setFile(f ?? null);
    setUpdateResult(null);
  };

  const handleUpdate = useCallback(async () => {
    if (!selectedId || !file) return;
    setUploading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = new Workbook();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.getWorksheet(1);
      const rows = [];
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const rateId = row.getCell(1).value;
        if (!rateId) return;
        rows.push({
          rate_id: String(rateId),
          hs_code: String(row.getCell(2).value ?? ""),
          commodity: String(row.getCell(3).value ?? ""),
          port_of_loading: String(row.getCell(5).value ?? ""),
          port_of_discharge: String(row.getCell(6).value ?? ""),
          port_of_delivery: String(row.getCell(7).value ?? ""),
          final_destination: String(row.getCell(8).value ?? ""),
          services_type: String(row.getCell(9).value ?? ""),
          rate_base: String(row.getCell(10).value ?? ""),
          selling_rate: String(row.getCell(11).value ?? ""),
        });
      });
      if (!rows.length) throw new Error("ไม่พบข้อมูลใน Excel หรือไม่ใช่ไฟล์ที่ Export จากระบบ");
      const res = await fetch(`${api_path}/us-shipment/update-snapshot.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ snapshot_id: selectedId, file_name: file.name, data: rows }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error ?? "Update failed");
      setUpdateResult(json);
      showToast(`✅ Update สำเร็จ: แก้ไข ${json.updated} · ลบ ${json.deleted ?? 0} · ไม่เปลี่ยน ${json.unchanged} row`);
    } catch (e) {
      showToast("❌ Update ไม่สำเร็จ: " + e.message, "error");
    }
    setUploading(false);
  }, [selectedId, file, showToast]);

  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1300, background: "#0006", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "min(600px, 95vw)", boxShadow: "0 8px 40px #0003" }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 800, fontSize: 16, color: "#1e293b" }}>📤 Update Snapshot</span>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}>✕</button>
        </div>
        <div style={{ padding: "24px" }}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontWeight: 700, fontSize: 13, color: "#374151", display: "block", marginBottom: 6 }}>1. เลือก Snapshot ที่ต้องการ Update</label>
            {loadingSnap ? <div style={{ color: "#64748b", fontSize: 13 }}>⏳ กำลังโหลด...</div> : (
              <select value={selectedId} onChange={e => { setSelectedId(e.target.value); setUpdateResult(null); }}
                style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, color: "#1e293b" }}>
                <option value="">-- เลือก Snapshot --</option>
                {snapshots.map(s => (
                  <option key={s.id} value={s.id}>{s.file_name} | {s.created_at} | {s.row_count?.toLocaleString()} rows</option>
                ))}
              </select>
            )}
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontWeight: 700, fontSize: 13, color: "#374151", display: "block", marginBottom: 6 }}>2. Upload ไฟล์ Excel ที่แก้ไขแล้ว (.xlsx)</label>
            <div onClick={() => fileInputRef.current?.click()}
              style={{ border: "2px dashed #cbd5e1", borderRadius: 8, padding: "20px", textAlign: "center", cursor: "pointer", background: "#f8fafc", color: "#64748b", fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "#6366f1"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "#cbd5e1"}>
              {file ? <span style={{ color: "#16a34a", fontWeight: 700 }}>📄 {file.name}</span> : <span>📂 คลิกเพื่อเลือกไฟล์ .xlsx</span>}
            </div>
            <input ref={fileInputRef} type="file" accept=".xlsx" onChange={handleFileChange} style={{ display: "none" }} />
          </div>
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "10px 14px", fontSize: 12, color: "#92400e", marginBottom: 20 }}>
            💡 ระบบจะ update เฉพาะ row ที่มีการเปลี่ยนแปลงโดยใช้ <strong>Rate ID</strong> เป็น key และบันทึก log การเปลี่ยนแปลงทุก field ไว้ทั้งหมด
          </div>
          {updateResult && (
            <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "12px 16px", marginBottom: 20, fontSize: 13 }}>
              <div style={{ fontWeight: 700, color: "#15803d", marginBottom: 6 }}>✅ Update เสร็จสิ้น</div>
              <div style={{ color: "#374151" }}>
                • แก้ไข: <strong>{updateResult.updated}</strong> row &nbsp;|&nbsp;
                ลบ: <strong>{updateResult.deleted ?? 0}</strong> row &nbsp;|&nbsp;
                ไม่เปลี่ยน: <strong>{updateResult.unchanged}</strong> row &nbsp;|&nbsp;
                ไม่พบใน snapshot: <strong>{updateResult.not_found ?? 0}</strong> row
              </div>
              {updateResult.log_id && <div style={{ color: "#64748b", fontSize: 12, marginTop: 4 }}>Log ID: #{updateResult.log_id}</div>}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={onClose} style={{ padding: "8px 20px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>ปิด</button>
            <button onClick={handleUpdate} disabled={!selectedId || !file || uploading}
              style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: (!selectedId || !file || uploading) ? "#e2e8f0" : "#6366f1", color: (!selectedId || !file || uploading) ? "#94a3b8" : "#fff", fontWeight: 700, cursor: (!selectedId || !file || uploading) ? "default" : "pointer", fontSize: 13 }}>
              {uploading ? "⏳ กำลัง Update..." : "📤 Update Snapshot"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export function USAShipmentDatagrid(props) {
  const [, height] = useDeviceSize();
  const classes = useStyles();
  const gridRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [snapshotInfo, setSnapshotInfo] = useState(null);
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [diffMode, setDiffMode] = useState(false);
  const [toast, setToast] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showUpdateLogModal, setShowUpdateLogModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ── 1. Fetch data ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ year: selectedYear, month: selectedMonth });
      const res = await fetch(`${api_path}/us-shipment/get-shipment.php?${p}`, {
        credentials: "include",
        headers: { "Authorization": token ? `Bearer ${token}` : "", "Content-Type": "application/json" },
      });
      const json = await res.json();
      const data = json.success ? (json.data ?? []) : [];
      setRawData(data); setDisplayData(data); setSummary(null);
      setSnapshotInfo(null); setDiffMode(false); setActiveFilter("ALL");
    } catch (err) {
      console.error(err); setRawData([]); setDisplayData([]);
      showToast("❌ โหลดข้อมูลไม่สำเร็จ", "error");
    } finally { setLoading(false); }
  }, [selectedYear, selectedMonth, showToast]);

 // useEffect(() => { fetchData(); }, []);

  // ── 2. Compare ──
  const handleCompare = useCallback(async () => {
    if (!rawData.length) return;
    setComparing(true);
    try {
      const res = await fetch(`${api_path}/us-shipment/compare-live.php`, {
      method: "POST",
      credentials: "include",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ data: rawData }),
    });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      setDisplayData(json.results ?? []); setSummary(json.summary ?? null);
      setSnapshotInfo(json.snapshot_label ?? null); setDiffMode(true); setActiveFilter("NEW");
      const s = json.summary ?? {}, changes = (s.changed ?? 0) + (s.new ?? 0) + (s.removed ?? 0);
      showToast(changes > 0 ? `🔍 พบการเปลี่ยนแปลง ${changes} รายการ` : "✅ ข้อมูลเหมือนเดิมทุกรายการ");
    } catch (e) { showToast("❌ เปรียบเทียบไม่สำเร็จ: " + e.message, "error"); }
    setComparing(false);
  }, [rawData, showToast]);
const handleSaveSnapshot = useCallback(async () => {
  const dataToSave = activeFilter === "ALL"
    ? displayData
    : displayData.filter(r => r.diff_status === activeFilter);

  if (!dataToSave.length) {
    showToast("❌ ไม่มีข้อมูลที่จะบันทึก", "error");
    return;
  }

  // ── Confirm ด้วย sweetalert ──
  const confirmed = await swal({
    title: "ยืนยันการบันทึก Snapshot?",
    text: `กรอง: ${activeFilter === "ALL" ? "ทั้งหมด" : activeFilter}  |  จำนวน: ${dataToSave.length.toLocaleString()} รายการ`,
    icon: "warning",
    buttons: {
      cancel: { text: "Cancel", value: false, visible: true },
      confirm: { text: "Confirm", value: true, className: "swal-button--confirm" },
    },
    dangerMode: false,
  });
  if (!confirmed) return;

  setSaving(true);
  try {
    const fileName = `Snapshot_${formatDate(new Date())}`;
    const res = await fetch(`${api_path}/us-shipment/save-snapshot.php`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data: dataToSave, file_name: fileName, user_id: getUserID ?? null }),
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.error ?? "Save failed");

    const snapRes  = await fetch(`${api_path}/us-shipment/get-snapshot.php?id=${json.snapshot_id}`);
    const snapJson = await snapRes.json();
    if (!snapJson.success) throw new Error(snapJson.error ?? "Load snapshot failed");
    await exportToExcel(snapJson.data, `${fileName}.xlsx`);

    showToast(`💾 บันทึก Snapshot สำเร็จ ${json.rows} รายการ · Export Excel แล้ว`);
    setDisplayData(rawData); setSummary(null); setSnapshotInfo(null); setDiffMode(false); setActiveFilter("ALL");
  } catch (e) { showToast("❌ บันทึกไม่สำเร็จ: " + e.message, "error"); }
  setSaving(false);
}, [activeFilter, displayData, rawData, showToast]);
  // // ── 3. Save Snapshot — บันทึกเฉพาะ row ที่ filter อยู่ ──
  // const handleSaveSnapshot = useCallback(async () => {
  // const dataToSave = activeFilter === "ALL"
  //   ? displayData
  //   : displayData.filter(r => r.diff_status === activeFilter);

  // if (!dataToSave.length) {
  //   showToast("❌ ไม่มีข้อมูลที่จะบันทึก", "error");
  //   return;
  // }

  // // ── Confirm ก่อนบันทึก ──
  // const filterLabel = activeFilter === "ALL" ? "ทั้งหมด" : activeFilter;
  // const confirmed = window.confirm(
  //   `⚠️ ยืนยันการบันทึก Snapshot?\n\n` +
  //   `• กรอง: ${filterLabel}\n` +
  //   `• จำนวน: ${dataToSave.length} รายการ\n\n` +
  //   `กด OK เพื่อยืนยัน`
  // );
  // if (!confirmed) return;

  // setSaving(true);
  //   try {
  //     const fileName = `Snapshot_${formatDate(new Date())}`;
  //     const res = await fetch(`${api_path}/us-shipment/save-snapshot.php`, {
  //       method: "POST", headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({ data: dataToSave, file_name: fileName, user_id: getUserID ?? null }),
  //     });
  //     const json = await res.json();
  //     if (!json.success) throw new Error(json.error ?? "Save failed");

  //     // ดึงข้อมูลกลับจาก DB (มี id แล้ว) แล้ว export
  //     const snapRes = await fetch(`${api_path}/us-shipment/get-snapshot.php?id=${json.snapshot_id}`);
  //     const snapJson = await snapRes.json();
  //     if (!snapJson.success) throw new Error(snapJson.error ?? "Load snapshot failed");
  //     await exportToExcel(snapJson.data, `${fileName}.xlsx`);

  //     showToast(`💾 บันทึก Snapshot สำเร็จ ${json.rows} รายการ · Export Excel แล้ว`);
  //     setDisplayData(rawData); setSummary(null); setSnapshotInfo(null); setDiffMode(false); setActiveFilter("ALL");
  //   } catch (e) { showToast("❌ บันทึกไม่สำเร็จ: " + e.message, "error"); }
  //   setSaving(false);
  // }, [activeFilter, displayData, rawData, showToast]);

  // ── computed ──
  const filteredData = activeFilter === "ALL"
    ? displayData
    : displayData.filter(r => r.diff_status === activeFilter);

  const onRowPrepared = useCallback((e) => {
    if (e.rowType !== "data" || !diffMode) return;
    const s = STATUS_META[e.data?.diff_status];
    if (s && s.bg !== "transparent") {
      e.rowElement.style.backgroundColor = s.bg;
      e.rowElement.style.borderLeft = `3px solid ${s.color}`;
    }
  }, [diffMode]);
  const onCellPrepared = useCallback((e) => {
    if (e.rowType !== "data" || !diffMode || e.data?.diff_status !== "CHANGED") return;
    const changedFields = e.data?.changed_fields ?? [], field = e.column?.dataField;
    if (field && COMPARE_FIELDS.includes(field) && changedFields.includes(field)) {
      e.cellElement.style.background = "#fef9c3";
      e.cellElement.style.fontWeight = "700";
      e.cellElement.style.color = "#92400e";
      e.cellElement.style.outline = "1px solid #f59e0b";
      e.cellElement.style.outlineOffset = "-1px";
      const oldVal = e.data?.old_values?.[field];
      if (oldVal != null && oldVal !== "") e.cellElement.title = `เดิม: ${oldVal}`;
    }
  }, [diffMode]);

  const onExporting = useCallback((e) => {
    const fileName = `US_Shipment_${formatDate(new Date())}.xlsx`;
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("US Shipment");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true }).then(() => {
      workbook.xlsx.writeBuffer().then(buffer =>
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName)
      );
    });
  }, []);

  // ── Search + Compare รวมในปุ่มเดียว ──
  const handleSearchAndCompare = useCallback(async () => {
    setLoading(true);
    let data = [];
    try {
      const p = new URLSearchParams({ year: selectedYear, month: selectedMonth });
      const res = await fetch(`${api_path}/us-shipment/get-shipment.php?${p}`, {
        credentials: "include",
        headers: { "Authorization": token ? `Bearer ${token}` : "", "Content-Type": "application/json" },
      });
      const json = await res.json();
      data = json.success ? (json.data ?? []) : [];
      setRawData(data);
    } catch (err) {
      console.error(err);
      showToast("❌ โหลดข้อมูลไม่สำเร็จ", "error");
      setLoading(false);
      return;
    } finally { setLoading(false); }

    // compare ทันทีหลัง fetch สำเร็จ
    if (!data.length) {
      setDisplayData([]); setSummary(null); setSnapshotInfo(null); setDiffMode(false); setActiveFilter("ALL");
      showToast("ไม่พบข้อมูลในช่วงเวลาที่เลือก", "error");
      return;
    }
    setComparing(true);
    // try {
    //   const res = await fetch(`${api_path}/us-shipment/compare-live.php`, {
    //     method: "POST", headers: { "Content-Type": "application/json" },
    //     body: JSON.stringify({ data }),
    //   });

          try {
      const res = await fetch(`${api_path}/us-shipment/compare-live.php`, {
      method: "POST",
      credentials: "include",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ data: data }),
    });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      setDisplayData(json.results ?? []); setSummary(json.summary ?? null);
      setSnapshotInfo(json.snapshot_label ?? null); setDiffMode(true); setActiveFilter("NEW");
      const s = json.summary ?? {}, changes = (s.changed ?? 0) + (s.new ?? 0) + (s.removed ?? 0);
      showToast(changes > 0 ? `🔍 พบการเปลี่ยนแปลง ${changes} รายการ` : "✅ ข้อมูลเหมือนเดิมทุกรายการ");
    } catch (e) { showToast("❌ เปรียบเทียบไม่สำเร็จ: " + e.message, "error"); }
    setComparing(false);
  }, [selectedYear, selectedMonth, showToast]);
  // const handleClearFilter = useCallback(() => {
  //   setSelectedYear(now.getFullYear());
  //   setSelectedMonth(now.getMonth() + 1);
  // }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="xl">
      <Toast toast={toast} />
      <SnapshotLogModal open={showLogModal} onClose={() => setShowLogModal(false)} showToast={showToast} />
      <UpdateSnapshotModal open={showUpdateModal} onClose={() => setShowUpdateModal(false)} showToast={showToast} />
      <UpdateLogModal open={showUpdateLogModal} onClose={() => setShowUpdateLogModal(false)} showToast={showToast} />

      <Grid container spacing={2}><Grid item xs={12}>&nbsp;</Grid></Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10}>
          <label className="head">US Shipment - Ocean Freight Comparison</label>
        </Grid>
      </Grid>

      {/* Toolbar */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>

          {/* Year / Month Picker */}
          <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, color: "#1e293b" }}>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, color: "#1e293b" }}>
            {monthOptions.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>

          <Button1 variant="contained" onClick={handleSearchAndCompare}
            disabled={loading || comparing}
            style={{ background: (loading || comparing) ? "#e2e8f0" : "#6366f1", color: (loading || comparing) ? "#94a3b8" : "#fff", fontWeight: 700, textTransform: "none", fontSize: 13, boxShadow: "none" }}>
            {loading ? "⏳ กำลังโหลด..." : comparing ? "⏳ กำลังเปรียบเทียบ..." : "🔍 Search & เปรียบเทียบ Snapshot"}
          </Button1>
          {/* <Button1 className="searchButton" variant="outlined" color="primary" onClick={handleClearFilter}>Clear Filter</Button1>
          <Button1 className="searchButton" variant="outlined" color="primary" onClick={fetchData}>Refresh data</Button1> */}
          {/* <Button1 className="searchButton" variant="outlined" color="primary" onClick={() => window.location.reload()}>Reload page</Button1> */}


          <div style={{ width: 1, height: 28, background: "#e2e8f0", margin: "0 4px" }} />

          <Button1 variant="outlined" onClick={handleSaveSnapshot} disabled={saving || loading || !displayData.length}
            style={{ borderColor: "#22c55e", color: saving ? "#94a3b8" : "#16a34a", fontWeight: 700, textTransform: "none", fontSize: 13 }}>
            {saving ? "⏳ กำลังบันทึก..." : "💾 บันทึกเป็น Snapshot ใหม่"}
          </Button1>

          <Button1 variant="outlined" onClick={() => setShowLogModal(true)}
            style={{ borderColor: "#6366f1", color: "#6366f1", fontWeight: 700, textTransform: "none", fontSize: 13 }}>
            📋 ประวัติ Snapshot
          </Button1>

          <Button1 variant="outlined" onClick={() => setShowUpdateModal(true)}
            style={{ borderColor: "#f59e0b", color: "#d97706", fontWeight: 700, textTransform: "none", fontSize: 13 }}>
            📤 Update Snapshot
          </Button1>

          <Button1 variant="outlined" onClick={() => setShowUpdateLogModal(true)}
            style={{ borderColor: "#8b5cf6", color: "#7c3aed", fontWeight: 700, textTransform: "none", fontSize: 13 }}>
            📝 View Log
          </Button1>

          {diffMode && (
            <span style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>
              📊 Diff Mode
            </span>
          )}
          <Button1 className="searchButton" variant="outlined" color="primary" onClick={() => window.location.reload()}>Reload page</Button1>
          <FMCUserGuide mode="modal" />
        </Grid>
      </Grid>

      {diffMode && summary && (
        <SummaryBar summary={summary} activeFilter={activeFilter} onFilter={setActiveFilter} snapshotInfo={snapshotInfo} />
      )}
      {diffMode && (
        <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6, display: "flex", gap: 16 }}>
          <span>💡 <strong style={{ color: "#92400e" }}>เซลสีเหลือง</strong> = field ที่เปลี่ยนแปลง · Hover เพื่อดูค่าเดิม</span>
          <span>แสดง <strong>{filteredData.length}</strong> จาก <strong>{displayData.length}</strong> รายการ</span>
        </div>
      )}

      <Grid container className={classes.root}>
        <DataGrid
          ref={gridRef}
          dataSource={filteredData}
          keyExpr="title"
          showBorders={true}
          columnAutoWidth={false}
          rowAlternationEnabled={!diffMode}
          allowColumnReordering={true}
          allowColumnResizing={true}
          columnResizingMode="widget"
          columnMinWidth={50}
          showScrollbar={true}
          height={height}
          hoverStateEnabled={true}
          repaintChangesOnly={true}
          onExporting={onExporting}
          onRowPrepared={diffMode ? onRowPrepared : undefined}
          onCellPrepared={diffMode ? onCellPrepared : undefined}
          wordWrapEnabled={false}
          renderAsync={true}
          cacheEnabled={true}>
          <Scrolling mode="virtual" rowRenderingMode="virtual" showScrollbar="always" useNative={false} scrollByThumb={true} />
          <Paging defaultPageSize={50} />
          <Pager allowedPageSizes={PAGE_SIZES} showPageSizeSelector={true} showInfo={true} />
          <LoadPanel enabled={true} visible={loading || comparing} showIndicator={true} showPane={true} shading={true}
            message={comparing ? "กำลังเปรียบเทียบข้อมูล..." : "กำลังโหลดข้อมูล..."} />
          <FilterRow visible={true} />
          <FilterPanel visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} placeholder="Full Text Search..." />
          <ColumnChooser enabled={true} mode="select">
            <Position my="right top" at="right bottom" of=".dx-datagrid-column-chooser-button" />
            <ColumnChooserSearch enabled={true} editorOptions={SEARCH_OPTIONS} />
            <ColumnChooserSelection allowSelectAll={true} selectByClick={true} recursive={true} />
          </ColumnChooser>
          <StateStoring enabled={true} type="localStorage" storageKey="usShipmentGridState3" />
          <Export enabled={true} allowExportSelectedData={false} />

          {diffMode && (
            <Column caption="Status" width={90} fixed={true} fixedPosition="left"
              allowFiltering={false} allowSorting={false}
              cellRender={({ data }) => <StatusBadge data={data} />} />
          )}
          {COLUMNS.map(({ field, cap, w, fixed, hide, txtFmt }) => (
            <Column key={field} dataField={field} caption={cap} width={w}
              fixed={fixed ?? false} fixedPosition={fixed ? "left" : undefined}
              visible={!hide}
              customizeText={txtFmt === "currency" ? makeCurrencyText : undefined}
              alignment={txtFmt ? "right" : "left"} />
          ))}
        </DataGrid>
      </Grid>
    </Container>
  );
}
